import { useCallback, useState } from "react";

import { useKeyDown } from "~/lib/keydownHook";

export type ShiftClickFunction = "select" | "unselect";

export const useShiftSelect = <T>({
  rows,
  selectedRowIds,
  getRowId,
  onRowSelected
}: {
  rows: T[];
  selectedRowIds: string[];
  getRowId: (row: T) => string;
  onRowSelected: (row: T) => void;
}) => {
  const shiftIsPressed = useKeyDown("Shift");

  const [mostRecentClickIndex, setMostRecentClickIndex] = useState<
    number | null
  >(null);
  const [shiftClickFunction, setShiftClickFunction] =
    useState<ShiftClickFunction>("select");

  const handleRowClick = useCallback(
    (clickedRow: T) => {
      const clickedIndex = rows.findIndex(
        (row) => getRowId(row) === getRowId(clickedRow)
      );
      setMostRecentClickIndex(clickedIndex);

      const clickedItemIsSelected = selectedRowIds.includes(
        getRowId(clickedRow)
      );

      if (!shiftIsPressed) {
        setShiftClickFunction(clickedItemIsSelected ? "unselect" : "select");
      }

      if (shiftIsPressed && mostRecentClickIndex != null) {
        const start = Math.min(mostRecentClickIndex, clickedIndex);
        const end = Math.max(mostRecentClickIndex, clickedIndex);

        const selectedIndexRange = Array.from(
          { length: end - start + 1 },
          (_x, i) => i + start
        );

        const selectedIndexes = selectedRowIds.map((selectedRowId) =>
          rows.findIndex((row) => getRowId(row) === selectedRowId)
        );

        const indexesToSelect = selectedIndexRange.filter((index) =>
          shiftClickFunction === "select"
            ? !selectedIndexes.includes(index)
            : selectedIndexes.includes(index)
        );

        indexesToSelect.forEach((rowIndex: number) => {
          onRowSelected(rows[rowIndex]);
        });
      } else {
        onRowSelected(clickedRow);
      }
    },
    [
      rows,
      selectedRowIds,
      getRowId,
      shiftIsPressed,
      mostRecentClickIndex,
      shiftClickFunction,
      onRowSelected
    ]
  );

  return {
    shiftClickFunction,
    shiftIsPressed,
    handleRowClick
  };
};
